// src/modules/operaciones/inventarios/dashboard/ChartCard.js
//
// Contenedor reutilizable para los gráficos del dashboard: título, subtítulo, estado vacío
// y botón de pantalla completa (modal con Esc / clic fuera para cerrar).
//
// USO
//   // Gráfico de Recharts: la tarjeta ya lo envuelve en <ResponsiveContainer>, así que
//   // el hijo es directamente el gráfico (BarChart, PieChart, ComposedChart...).
//   <ChartCard titulo="Merma por bodega" subtitulo="Conocida vs desconocida ($)" vacio={datos.length === 0}>
//     <BarChart data={datos}>...</BarChart>
//   </ChartCard>
//
//   // Contenido que NO es un gráfico de Recharts (mapa de calor, tabla): responsive={false}
//   <ChartCard titulo="Mapa de calor" responsive={false} height="auto">
//     <MapaCalor ... />
//   </ChartCard>
//
//   // Si el gráfico debe cambiar en pantalla completa (más barras, más etiquetas), usa una función:
//   <ChartCard titulo="Top artículos">
//     {({ pantallaCompleta }) => <BarChart data={pantallaCompleta ? top30 : top10}>...</BarChart>}
//   </ChartCard>
//
// PROPS
//   titulo, subtitulo      texto del encabezado (el subtítulo es opcional y más pequeño)
//   children               gráfico (elemento) o función ({ pantallaCompleta }) => elemento
//   className              clases extra para la tarjeta (p. ej. "lg:col-span-2")
//   height                 alto del área del gráfico: número (px) o texto ("320px", "auto"). Defecto 320
//   vacio                  true → muestra el mensaje con icono en lugar del gráfico
//   mensajeVacio           texto del estado vacío
//   pantallaCompleta       false → oculta el botón de pantalla completa (defecto true)
//   responsive             false → no envuelve el contenido en ResponsiveContainer (defecto true)
//   acciones               nodo opcional junto al botón (selector, toggle...)
//   pie                    nodo opcional debajo del gráfico (nota, leyenda propia...)

import React, { useCallback, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { ResponsiveContainer } from "recharts";
import { BarChart3, Maximize2, X } from "lucide-react";

/* ========================================================================== */
/* Piezas internas                                                            */
/* ========================================================================== */

const aCss = (alto) => (typeof alto === "number" ? `${alto}px` : alto);

function Encabezado({ titulo, subtitulo, idTitulo, acciones, botonPantalla }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h3 id={idTitulo} className="text-sm font-semibold text-gray-900 leading-snug">
          {titulo}
        </h3>
        {subtitulo && <p className="text-xs text-gray-500 mt-0.5 leading-snug">{subtitulo}</p>}
      </div>
      {(acciones || botonPantalla) && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {acciones}
          {botonPantalla}
        </div>
      )}
    </div>
  );
}

function EstadoVacio({ mensaje, alto }) {
  return (
    <div
      className="flex flex-col items-center justify-center text-center rounded-lg bg-gray-50 border border-dashed border-gray-200 px-4"
      style={{ height: alto === "auto" ? "10rem" : alto }}
    >
      <BarChart3 className="w-8 h-8 text-gray-300 mb-2" aria-hidden="true" />
      <p className="text-sm text-gray-500">{mensaje}</p>
    </div>
  );
}

function BotonIcono({ onClick, etiqueta, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={etiqueta}
      aria-label={etiqueta}
      className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {children}
    </button>
  );
}

/* ========================================================================== */
/* Componente                                                                 */
/* ========================================================================== */

export function ChartCard({
  titulo,
  subtitulo = null,
  children,
  className = "",
  height = 320,
  vacio = false,
  mensajeVacio = "No hay datos para mostrar con los filtros actuales.",
  pantallaCompleta = true,
  responsive = true,
  acciones = null,
  pie = null,
}) {
  const [ampliado, setAmpliado] = useState(false);
  const idTitulo = useId();

  const abrir = useCallback(() => setAmpliado(true), []);
  const cerrar = useCallback(() => setAmpliado(false), []);

  // Pantalla completa: cierra con Esc y bloquea el scroll de la página mientras está abierta
  useEffect(() => {
    if (!ampliado) return undefined;
    const alPresionar = (e) => {
      if (e.key === "Escape") cerrar();
    };
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", alPresionar);
    return () => {
      document.body.style.overflow = overflowPrevio;
      document.removeEventListener("keydown", alPresionar);
    };
  }, [ampliado, cerrar]);

  // Si los datos se vacían mientras está ampliada, se cierra
  useEffect(() => {
    if (vacio && ampliado) setAmpliado(false);
  }, [vacio, ampliado]);

  const contenido = (esAmpliado) => {
    const hijo = typeof children === "function" ? children({ pantallaCompleta: esAmpliado }) : children;
    return responsive ? (
      <ResponsiveContainer width="100%" height="100%">
        {hijo}
      </ResponsiveContainer>
    ) : (
      hijo
    );
  };

  const puedeAmpliar = pantallaCompleta && !vacio;
  const botonAmpliar = puedeAmpliar ? (
    <BotonIcono onClick={abrir} etiqueta="Ver en pantalla completa">
      <Maximize2 className="w-4 h-4" />
    </BotonIcono>
  ) : null;

  return (
    <>
      <section
        className={`bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col ${className}`}
        aria-labelledby={idTitulo}
      >
        <Encabezado
          titulo={titulo}
          subtitulo={subtitulo}
          idTitulo={idTitulo}
          acciones={acciones}
          botonPantalla={botonAmpliar}
        />

        {vacio ? (
          <EstadoVacio mensaje={mensajeVacio} alto={aCss(height)} />
        ) : (
          <div style={{ height: aCss(height), minWidth: 0 }}>{contenido(false)}</div>
        )}

        {pie && !vacio && <div className="mt-2 text-xs text-gray-500">{pie}</div>}
      </section>

      {ampliado &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-3 sm:p-6"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) cerrar();
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={titulo}
              className="bg-white rounded-xl shadow-2xl w-full max-w-6xl p-4 sm:p-6 flex flex-col"
              style={{ maxHeight: "92vh" }}
            >
              <Encabezado
                titulo={titulo}
                subtitulo={subtitulo}
                idTitulo={`${idTitulo}-modal`}
                acciones={acciones}
                botonPantalla={
                  <BotonIcono onClick={cerrar} etiqueta="Cerrar pantalla completa">
                    <X className="w-5 h-5" />
                  </BotonIcono>
                }
              />
              <div style={{ height: "min(75vh, 760px)", minWidth: 0 }} className="overflow-auto">
                {contenido(true)}
              </div>
              {pie && <div className="mt-3 text-xs text-gray-500">{pie}</div>}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default ChartCard;